import {Tooltip} from 'platform/components';
import {
  Box,
  getColorByPath,
  getSize,
  Icon,
  Show,
  Text,
  ThemeColorPath,
  ThemeColorTextPath,
  VStack,
} from 'platform/foundation';
import styled from 'styled-components';
import {match} from 'ts-pattern';

import {ReactNode} from 'react';

import {FinancingMatrixCellState} from './types/FinancingMatrixCellState';

interface CellAppearance {
  backgroundColor: ThemeColorPath;
  hoverBackgroundColor: ThemeColorPath;
  borderColor: ThemeColorPath;
  primaryTextColor: ThemeColorTextPath;
}

const CELL_APPEARANCE = {
  ACTIVE: {
    backgroundColor: 'palettes.yellow.20.100',
    hoverBackgroundColor: 'palettes.yellow.30.100',
    borderColor: 'palettes.orange.60.100',
    primaryTextColor: 'warning',
  },
  CUSTOM: {
    backgroundColor: 'palettes.purple.20.100',
    hoverBackgroundColor: 'palettes.purple.30.100',
    borderColor: 'palettes.purple.60.100',
    primaryTextColor: 'link',
  },
  CONFIGURED: {
    backgroundColor: 'palettes.blue.20.100',
    hoverBackgroundColor: 'palettes.blue.30.100',
    borderColor: 'palettes.blue.40.100',
    primaryTextColor: 'link',
  },
  EMPTY: {
    backgroundColor: 'palettes.neutral.20.100',
    hoverBackgroundColor: 'palettes.neutral.30.100',
    borderColor: 'palettes.neutral.40.100',
    primaryTextColor: 'link',
  },
} as const satisfies Record<FinancingMatrixCellState, CellAppearance>;

const SELECTED_BORDER_COLOR = 'palettes.blue.60.100';

const CELL_HEIGHT = 15;
const CELL_PADDING = 1;
const CELL_BORDER_WIDTH = '1px';

interface CellSurfaceProps {
  $backgroundColor: ThemeColorPath;
  $hoverBackgroundColor: ThemeColorPath;
  $borderColor: ThemeColorPath;
  $borderStyle: 'solid' | 'dashed';
  $isDisabled: boolean;
}

/**
 * The cell's own interactive surface — the shared `Clickable` has no disabled a11y contract
 * (`aria-disabled`, forced `tabindex="-1"`), so the cell carries its semantics itself.
 *
 * The hover tint is CSS-only on purpose. Holding it in React state re-rendered every cell of the
 * matrix on each pointer move, and the matrix renders up to sixty cells at once. A disabled cell
 * gets `pointer-events: none`, so it never matches `:hover` — the same behaviour the
 * state-driven version had.
 */
const CellSurface = styled.div<CellSurfaceProps>`
  position: relative;
  /* Shift-activating a cell toggles the bulk-edit selection; without this, shift-click also
     drag-selects the cell text. */
  user-select: none;
  cursor: ${({$isDisabled}) => ($isDisabled ? 'initial' : 'pointer')};
  pointer-events: ${({$isDisabled}) => ($isDisabled ? 'none' : 'all')};
  height: ${getSize(CELL_HEIGHT)};
  padding: ${getSize(CELL_PADDING)};
  border: ${CELL_BORDER_WIDTH} ${({$borderStyle}) => $borderStyle};
  border-color: ${({$borderColor, theme}) => getColorByPath($borderColor, theme)};
  border-radius: ${({theme}) => theme.radii.small};
  background-color: ${({$backgroundColor, theme}) => getColorByPath($backgroundColor, theme)};

  &:hover {
    background-color: ${({$hoverBackgroundColor, theme}) =>
      getColorByPath($hoverBackgroundColor, theme)};
  }

  /* The design-system focus ring: theme.shadows.active is the semantic focus/active token every
     platform input shares, so a keyboard-focused cell reads the same as a focused input instead of
     falling back to the browser's default outline. */
  &:focus-visible {
    outline: none;
    box-shadow: ${({theme}) => theme.shadows.active};
  }
`;

interface FinancingMatrixCellProps {
  state: FinancingMatrixCellState;
  primaryValue?: string;
  secondaryValue?: string;
  tertiaryValue?: string | null;
  emptyLabel: string;
  ariaLabel?: string;
  isDisabled: boolean;
  isSelected?: boolean;
  tooltip?: ReactNode;
  onActivate: (options: {isMultiSelect: boolean}) => void;
}

export function FinancingMatrixCell(props: FinancingMatrixCellProps) {
  const isEmpty = props.state === 'EMPTY';
  const appearance = match(props.state)
    .with('ACTIVE', () => CELL_APPEARANCE.ACTIVE)
    .with('CUSTOM', () => CELL_APPEARANCE.CUSTOM)
    .with('CONFIGURED', () => CELL_APPEARANCE.CONFIGURED)
    .with('EMPTY', () => CELL_APPEARANCE.EMPTY)
    .exhaustive();

  const cell = (
    // `aria-pressed` — not `aria-selected` — is deliberate. Shift-activating a cell toggles it in
    // and out of the bulk-edit selection, which is exactly a toggle button. `aria-selected` is only
    // valid on `gridcell`/`option`/`row`/`tab`/`treeitem`, and the matrix is a plain layout table
    // without roving tabindex or arrow-key navigation, so claiming `role="grid"` would advertise a
    // navigation model this component does not implement.
    <CellSurface
      role="button"
      aria-label={props.ariaLabel ?? (isEmpty ? props.emptyLabel : undefined)}
      aria-pressed={props.isSelected}
      aria-disabled={props.isDisabled || undefined}
      tabIndex={props.isDisabled ? -1 : 0}
      $isDisabled={props.isDisabled}
      $backgroundColor={appearance.backgroundColor}
      $hoverBackgroundColor={appearance.hoverBackgroundColor}
      $borderColor={props.isSelected ? SELECTED_BORDER_COLOR : appearance.borderColor}
      $borderStyle={isEmpty && !props.isSelected ? 'dashed' : 'solid'}
      onKeyDown={(event) => {
        if (props.isDisabled || (event.key !== 'Enter' && event.key !== ' ')) {
          return;
        }

        event.preventDefault();
        props.onActivate({isMultiSelect: event.shiftKey});
      }}
      onClick={
        props.isDisabled ? undefined : (event) => props.onActivate({isMultiSelect: event.shiftKey})
      }
    >
      <Show when={!isEmpty}>
        <VStack height="100%" spacing={0} align="center" justify="center">
          <Text size="small" color={appearance.primaryTextColor} alternative>
            {props.primaryValue}
          </Text>
          <Text size="xxSmall" color="secondary">
            {props.secondaryValue}
          </Text>
          <Show when={props.tertiaryValue}>
            <Text size="xSmall" color="primary" alternative>
              {props.tertiaryValue}
            </Text>
          </Show>
        </VStack>
      </Show>
      <Show when={isEmpty}>
        <VStack height="100%" align="center" justify="center">
          <Icon
            value={props.isSelected ? 'action/check_circle' : 'content/add'}
            color={props.isSelected ? 'general.accent' : 'text.tertiary'}
            size={4}
          />
        </VStack>
      </Show>
      <Show when={props.isSelected && !isEmpty}>
        <Box position="absolute" top={1} right={1}>
          <Icon value="action/check_circle" color="general.accent" size={3} />
        </Box>
      </Show>
    </CellSurface>
  );

  return (
    <Tooltip
      description={props.tooltip}
      placement="top"
      hasAutoWidth
      shouldWrapperStretch
      isDisabled={isEmpty || !props.tooltip}
    >
      {cell}
    </Tooltip>
  );
}
